import type { ApiResponse } from "../../../types/shared";
import httpClient from "../../../utils/httpClient";
import type { AuthTokens, Profile } from "../interfaces";

interface IAuthService {
  signup(params: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    companyName: string;
    websiteUrl?: string;
  }): Promise<{ userId: number }>;

  login(email: string, password: string): Promise<AuthTokens>;

  verifyEmail(token: string): Promise<AuthTokens>;

  forgotPassword(email: string): Promise<void>;

  resetPassword(token: string, password: string): Promise<void>;

  refreshToken(refreshToken: string): Promise<AuthTokens>;

  logout(refreshToken: string): Promise<void>;

  resendVerificationEmail(email: string): Promise<void>;

  getProfile(): Promise<Profile>;

  updateProfile(data: {
    firstName?: string;
    lastName?: string;
  }): Promise<Partial<Profile>>;

  logoutAll(): Promise<void>;

  resendVerificationAuthenticated(): Promise<void>;
}

class AuthService implements IAuthService {
  private base = "/v1/auth";

  signup = async (params: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    companyName: string;
    websiteUrl?: string;
  }): Promise<{ userId: number }> => {
    const res = await httpClient.post<
      ApiResponse<{ message: string; userId: number }>
    >(`${this.base}/signup`, params);

    return res.data.data;
  };

  login = async (email: string, password: string): Promise<AuthTokens> => {
    const res = await httpClient.post<ApiResponse<AuthTokens>>(
      `${this.base}/login`,
      { email, password }
    );

    return res.data.data;
  };

  verifyEmail = async (token: string): Promise<AuthTokens> => {
    const res = await httpClient.post<ApiResponse<AuthTokens>>(
      `${this.base}/verify-email`,
      { token }
    );

    return res.data.data;
  };

  forgotPassword = async (email: string): Promise<void> => {
    await httpClient.post<ApiResponse<{ message: string }>>(
      `${this.base}/forgot-password`,
      { email }
    );
  };

  resetPassword = async (token: string, password: string): Promise<void> => {
    await httpClient.post<ApiResponse<{ message: string }>>(
      `${this.base}/reset-password`,
      { token, password }
    );
  };

  refreshToken = async (refreshToken: string): Promise<AuthTokens> => {
    const res = await httpClient.post<ApiResponse<AuthTokens>>(
      `${this.base}/refresh-token`,
      { refreshToken }
    );

    return res.data.data;
  };

  logout = async (refreshToken: string): Promise<void> => {
    await httpClient.post<ApiResponse<{ message: string }>>(
      `${this.base}/logout`,
      { refreshToken }
    );
  };

  resendVerificationEmail = async (email: string): Promise<void> => {
    await httpClient.post<ApiResponse<{ message: string }>>(
      `${this.base}/resend-verification-email`,
      { email }
    );
  };

  getProfile = async (): Promise<Profile> => {
    const res = await httpClient.get<ApiResponse<Profile>>(
      `${this.base}/profile`
    );

    return res.data.data;
  };

  updateProfile = async (data: {
    firstName?: string;
    lastName?: string;
  }): Promise<Partial<Profile>> => {
    const res = await httpClient.put<ApiResponse<Partial<Profile>>>(
      `${this.base}/profile`,
      data
    );

    return res.data.data;
  };

  logoutAll = async (): Promise<void> => {
    await httpClient.post<ApiResponse<{ message: string }>>(
      `${this.base}/logout-all`
    );
  };

  resendVerificationAuthenticated = async (): Promise<void> => {
    await httpClient.post<ApiResponse<{ message: string }>>(
      `${this.base}/resend-verification`
    );
  };
}

export const authService = new AuthService();
